import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { dataCy, SELECTORS } from '../../utils/dataCy';
import { useLogin } from '../../components/logIncontext';

function ContentSidebar() {
  const { t, i18n } = useTranslation('home');
  const { isLoggedIn, userData } = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const [openCategories, setOpenCategories] = useState({});
  const hasInitialized = useRef(false);

  const categories = t('categories', { returnObjects: true }) || [];

  useEffect(() => {
    if (!hasInitialized.current) {
      const initialOpen = {};

      categories.forEach((cat) => {
        // Si estamos en cualquier ruta de esta categoría, expandir toda la categoría
        if (location.pathname.includes(`/${cat.slug}/`)) {
          initialOpen[`category-${cat.slug}`] = true;
          
          // Expandir todas las subcategorías si las hay
          cat.subcategories?.forEach((sub) => {
            initialOpen[`${cat.slug}/${sub.slug}`] = true;
          });
        }
        
        // Lógica específica para artículos (mantener para casos específicos)
        cat.articles?.forEach((article) => {
          if (location.pathname.includes(`/${article.slug}`)) {
            initialOpen[`category-${cat.slug}`] = true;
          }
        });

        // Subcategorías
        cat.subcategories?.forEach((sub) => {
          sub.articles?.forEach((article) => {
            if (location.pathname.includes(`/${article.slug}`)) {
              initialOpen[`category-${cat.slug}`] = true;
              initialOpen[`${cat.slug}/${sub.slug}`] = true;
            }
          });
        });
      });

      setOpenCategories(initialOpen);
      hasInitialized.current = true;
    }
  }, [categories, location.pathname]);

  const toggleCategory = (key) => {
    setOpenCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleArticleClick = (categoryPath, articleSlug) => {
    navigate(`/${i18n.language}/${categoryPath}/${articleSlug}`);
  };

  return (
    <div className="p-4 sm:p-6 rounded-lg shadow-md sticky top-28 bg-gray-50 text-gray-800 dark:bg-grayButton dark:text-gray-200">
      <h2 className="font-bold text-lg mb-4 text-gray-800 dark:text-gray-100">
        {t('sidebar.title')}
      </h2>
      {categories.filter(category => {
        const formulasSlugs = ['formulas', 'formules', 'formule', 'formulas-zh'];
        return category.slug !== 'api' && !formulasSlugs.includes(category.slug);
      }).map((category) => (
        <div key={category.slug} className="mb-4">
          {/* Categoría principal */}
          <div
            className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
            onClick={() => toggleCategory(`category-${category.slug}`)}
            data-cy={dataCy(SELECTORS.BUTTON, `sidebar-category-${category.slug}`)}
          >
            <h3 className="text-base sm:text-lg">{category.title}</h3>
            {openCategories[`category-${category.slug}`] ? (
              <FaChevronUp />
            ) : (
              <FaChevronDown />
            )}
          </div>

          <AnimatePresence>
            {openCategories[`category-${category.slug}`] && (
              <motion.div
                className="ml-2 mt-2"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Artículos directos */}
                {category.articles?.map((article) => {
                  const path = `/${i18n.language}/${category.slug}/${article.slug}`;
                  const isSelected = location.pathname === path;
                  return (
                    <li
                      key={article.slug}
                      className={`flex py-1 items-center cursor-pointer hover:text-secondBg dark:hover:text-secondBg text-sm ${isSelected
                        ? 'font-bold text-secondBg'
                        : 'text-gray-700 dark:text-gray-300'
                        }`}
                      onClick={() => handleArticleClick(category.slug, article.slug)}
                      data-cy={dataCy(SELECTORS.BUTTON, `sidebar-article-${article.slug}`)}
                    >
                      {article.title}
                    </li>
                  );
                })}

                {/* Subcategorías */}
                {category.subcategories?.map((sub) => {
                  const subKey = `${category.slug}/${sub.slug}`;
                  return (
                    <div key={sub.slug} className="mt-2 ml-2">
                      <div
                        className="flex justify-between cursor-pointer text-gray-600 dark:text-gray-400 font-medium hover:text-secondBg dark:hover:text-secondBg"
                        onClick={() => toggleCategory(subKey)}
                      >
                        <h4 className="text-sm font-bold">{sub.title}</h4>
                        {openCategories[subKey] ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                      <AnimatePresence>
                        {openCategories[subKey] && (
                          <motion.ul
                            className="ml-4 mt-1"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {sub.articles?.map((article) => {
                              const fullPath = `/${i18n.language}/${category.slug}/${sub.slug}/${article.slug}`;
                              const isSelected = location.pathname === fullPath;
                              return (
                                <li
                                  key={article.slug}
                                  className={`flex py-1 items-center cursor-pointer hover:text-secondBg dark:hover:text-secondBg text-sm ${isSelected
                                    ? 'font-bold text-secondBg'
                                    : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  onClick={() =>
                                    handleArticleClick(`${category.slug}/${sub.slug}`, article.slug)
                                  }
                                >
                                  {article.title}
                                </li>
                              );
                            })}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}

    </div>
  );
}

export default ContentSidebar;
